import React, { useState, useEffect } from "react";
import PropTypes from "prop-types";
import {
    Backdrop,
    Card,
    IconButton,
    Box,
    Slide,
} from "@mui/material";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import CloseIcon from "@mui/icons-material/Close";
import dayjs from "dayjs";

import EntityGrowthChart from "examples/Charts/LineCharts/EntityGrowthChart";

// Hooks
import { useEntityMetrics } from "hooks/useEntityMetrics";
import { useEntityHistory } from "hooks/useEntityHistory";

// Sub-components
import EntityVisual from "./EntityVisual";
import EntityHeader from "./EntityHeader";
import EntityKeyMetrics from "./EntityKeyMetrics";
import EntityReactions from "./EntityReactions";
import EntityPerformanceRates from "./EntityPerformanceRates";

function EntityDetailsPopup({ isModalOpen, onClose, entityObject, useBackdrop = true }) {
    const [showCard, setShowCard] = useState(false);

    useEffect(() => {
        if (isModalOpen) {
            setShowCard(true);
        } else {
            const timeout = setTimeout(() => setShowCard(false), 300);
            return () => clearTimeout(timeout);
        }
    }, [isModalOpen]);

    const handleCloseClick = () => {
        setShowCard(false);
        setTimeout(onClose, 100);
    };

    // 1. Logic Hooks
    const {
        profileObject,
        metrics,
        reachLabel,
        impressionsLabel,
        hasActualReach,
        hasActualImpressions
    } = useEntityMetrics(entityObject);

    const chartData = useEntityHistory(isModalOpen, entityObject);

    if (!entityObject) return null;

    const formattedDate = dayjs(
        entityObject.created_time ||
        entityObject.publishedAt ||
        entityObject.taken_at
    ).format("MMM D, YYYY [at] h:mmA");

    // ------------------ CARD LAYOUT ------------------

    const CardContent = (
        <Slide direction="up" in={showCard} mountOnEnter unmountOnExit>
            <Box
                sx={{
                    position: "fixed",
                    bottom: 0,
                    left: 0,
                    width: "100%",
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "flex-end",
                    zIndex: (theme) => theme.zIndex.modal + 2,
                    pointerEvents: "none",
                }}
            >
                <Card
                    sx={{
                        pointerEvents: "auto",
                        width: { xs: "95%", sm: "90%", md: "90%", lg: "90%" },
                        maxWidth: "1400px",
                        maxHeight: "85vh",
                        overflowY: "auto",
                        p: 4,
                        borderRadius: 3,
                        boxShadow: 24,
                        display: "flex",
                        flexDirection: "column",
                        backgroundColor: "white",
                    }}
                >
                    <IconButton
                        onClick={handleCloseClick}
                        sx={{ position: "absolute", top: 8, right: 8, zIndex: 1301 }}
                    >
                        <CloseIcon />
                    </IconButton>

                    <MDBox
                        display="flex"
                        justifyContent="space-between"
                        alignItems="center"
                        mb={3}
                        sx={{
                            borderBottom: "1px solid #e0e0e0",
                            position: "sticky",
                            top: 0,
                            backgroundColor: "white",
                            zIndex: 1000,
                        }}
                    >
                        <MDTypography variant="h5">
                            Published on {formattedDate}
                        </MDTypography>
                    </MDBox>

                    <MDBox sx={{ flexGrow: 1, overflowY: "auto" }}>
                        <MDBox display="flex" flexDirection={{ xs: "column", md: "row" }} gap={3} alignItems="stretch">

                            {/* Left Column: Visual (Image/Video) */}
                            <EntityVisual entityObject={entityObject} />

                            {/* Right Column: Content */}
                            <MDBox
                                width={{ xs: "100%", md: "58.333333%" }}
                                sx={{
                                    display: "flex",
                                    flexDirection: "column",
                                }}
                            >
                                {/* Header Section */}
                                <EntityHeader
                                    profileObject={profileObject}
                                    entityObject={entityObject}
                                />

                                <MDBox sx={{ flex: 1, pr: 1 }}>
                                    <MDTypography
                                        variant="h6"
                                        sx={{
                                            borderBottom: "1px solid #e0e0e0",
                                            pb: 1,
                                            mb: 2,
                                            fontWeight: "medium"
                                        }}
                                    >
                                        Metrics
                                    </MDTypography>

                                    {/* HERO SECTION: Views, Reach, Engagement */}
                                    <EntityKeyMetrics
                                        metrics={metrics}
                                        hasActualImpressions={hasActualImpressions}
                                        hasActualReach={hasActualReach}
                                        reachLabel={reachLabel}
                                        impressionsLabel={impressionsLabel}
                                    />

                                    {/* ACTION BAR: Reactions, Comments, Shares */}
                                    <EntityReactions metrics={metrics} />

                                    {/* RATES BAR: Performance percentages */}
                                    <EntityPerformanceRates metrics={metrics} />

                                    {/* Extra Platform Specifics (Video Views) */}
                                    {metrics["Video Views"] && (
                                        <MDBox mt={2} p={1.5} border="1px dashed #dcdcdc" borderRadius="md" display="flex" justifyContent="space-between" alignItems="center">
                                            <MDTypography variant="button" fontWeight="medium">Specific Video Views</MDTypography>
                                            <MDTypography variant="h6">{metrics["Video Views"].toLocaleString()}</MDTypography>
                                        </MDBox>
                                    )}
                                    {metrics["Post Views"] && (
                                        <MDBox mt={2} p={1.5} border="1px dashed #dcdcdc" borderRadius="md" display="flex" justifyContent="space-between" alignItems="center">
                                            <MDTypography variant="button" fontWeight="medium">Post Views</MDTypography>
                                            <MDTypography variant="h6">{metrics["Post Views"].toLocaleString()}</MDTypography>
                                        </MDBox>
                                    )}
                                </MDBox>
                            </MDBox>
                        </MDBox>

                        {/* Growth Chart Section */}
                        {chartData && (
                            <MDBox mt={3}>
                                <EntityGrowthChart
                                    title={entityObject.platform === 'YouTube' ? "Video Growth" : "Post Growth"}
                                    description="Performance over time"
                                    chart={chartData}
                                />
                            </MDBox>
                        )}
                    </MDBox>
                </Card>
            </Box>
        </Slide >
    );

    return useBackdrop ? (
        <Backdrop
            open={isModalOpen || showCard}
            sx={{
                zIndex: (theme) => theme.zIndex.modal + 1,
                backgroundColor: "rgba(0, 0, 0, 0.85)",
                transition: "background-color 0.3s ease-in-out",
            }}
        >
            {CardContent}
        </Backdrop>
    ) : (
        CardContent
    );
}

EntityDetailsPopup.propTypes = {
    isModalOpen: PropTypes.bool.isRequired,
    onClose: PropTypes.func.isRequired,
    entityObject: PropTypes.object,
    useBackdrop: PropTypes.bool,
};

EntityDetailsPopup.defaultProps = {
    entityObject: null,
    useBackdrop: true,
};

export default EntityDetailsPopup;
